/// <reference types="vite/client" />
import * as Sentry from "@sentry/react";
import { StartClient } from "@tanstack/react-start/client";
import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";

import "@/web/global.css";
import "@/web/lib/polyfills/readableStream";
import { env } from "@/web/lib/env";

/**
 * Clerk's CDN script (`clerk.browser.js`) failing to load. This is now an
 * expected, self-healing condition: `ClerkProviderWithRetry` reloads it, and a
 * persistent failure only degrades the sign-in CTA (auth is client-only, the
 * rest of the page renders). We still want the volume signal, so we keep the
 * event rather than dropping it — but downgrade it and re-fingerprint it (see
 * `beforeSend`) so it stops reopening TYCHO-19 / TYCHO-1B and stops alerting.
 *
 * `originalException` carries Clerk's `ClerkRuntimeError` (`code`), but the
 * event is sometimes anchored on the chained "failed to load script" error, so
 * we also scan the serialized exception values.
 */
/**
 * Whether this browser is below the modern-JS baseline our stack hard-requires.
 * The SpacetimeDB SDK uses `Object.hasOwn` (Chrome 93 / Safari 15.4 / Firefox 92)
 * while building the DB connection, and other deps use `Array.prototype.at`
 * (Chrome 92). Browsers missing either — in practice only ancient HeadlessChrome
 * bots, zero real users — crash deep inside dependency code (TYCHO-1S:
 * `fz is not a function` from the SDK's `Object.hasOwn`; TYCHO-1T from `.at`).
 * Those crashes are unactionable: the app cannot run there at all, and the
 * minified frames churn across builds, so we drop the events by capability
 * rather than by signature.
 */
function isUnsupportedBrowser(): boolean {
  return typeof Object.hasOwn !== "function" || typeof Array.prototype.at !== "function";
}

function isClerkScriptLoadError(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  const original = hint.originalException as { code?: unknown } | null | undefined;
  if (original && typeof original === "object" && original.code === "failed_to_load_clerk_js") {
    return true;
  }
  return (event.exception?.values ?? []).some(
    (value) =>
      value.value?.includes("failed_to_load_clerk_js") ||
      value.value?.includes("Failed to load Clerk JS") ||
      value.value?.includes("clerk.browser.js"),
  );
}

Sentry.init({
  dsn: env.VITE_SENTRY_DSN,
  sendDefaultPii: false,
  environment: env.VITE_TYCHO_ENV,
  // tanstackRouterBrowserTracingIntegration intentionally dropped — the router
  // is now per-request via getRouter(); re-wire by passing it via router context
  // (see TANSTACK_START_MIGRATION.md Phase 6).
  integrations: [Sentry.replayIntegration()],
  tracesSampleRate: env.VITE_TYCHO_ENV === "prod" ? 0.1 : 1.0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  // Drop errors thrown inside Cloudflare's auto-injected Web Analytics beacon
  // (`beacon.min.js`) — code we neither ship nor control. Its CLS observer calls
  // `Array.prototype.at()`, which throws in the ancient HeadlessChrome 91 bots
  // that trip it (`this.i.at is not a function`, TYCHO-1T): third-party noise,
  // zero real users. Every frame is in `beacon.min.js`, so this matches without
  // touching our own errors.
  denyUrls: [/beacon\.min\.js/i],
  ignoreErrors: [
    // Outlook Safe Links / email-security scanners inject a script that rejects a
    // promise with this exact object when its RPC target is missing. Not our code,
    // no stack frames into our app, 0 users impacted (TYCHO-1E) — canonical noise.
    /Object Not Found Matching Id/,
    // AutoConsent (a cookie-banner auto-dismisser shipped in some browsers/
    // extensions, e.g. DuckDuckGo/Brave) runs from an injected `blob:` URL and
    // throws reading `undoPrehide` off an undefined element. Third-party, not our
    // code, 0 users (TYCHO-1R). `undoPrehide` is unique to it, so this is safe.
    /undoPrehide/,
  ],
  beforeSend(event, hint) {
    // Drop everything from browsers below our JS baseline — they cannot run the
    // app, so their crashes (TYCHO-1S/1T and future variants) are unactionable.
    if (isUnsupportedBrowser()) {
      return null;
    }
    if (isClerkScriptLoadError(event, hint)) {
      // Keep capturing for volume, but out of the error tier: `warning` keeps
      // it below level-based alerts, and the explicit fingerprint collapses
      // every occurrence into one dedicated issue that is distinct from the
      // default grouping of TYCHO-19 / TYCHO-1B — so those stay closed once
      // marked resolved instead of regressing on the next failed load.
      event.level = "warning";
      event.fingerprint = ["clerk-js-script-load-failure"];
      event.tags = { ...event.tags, clerk_script_load_failed: "true" };
    }
    return event;
  },
});

hydrateRoot(
  document,
  <StrictMode>
    <StartClient />
  </StrictMode>,
);
